import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ArrowRight, Sparkles, X } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { useMarketPartners } from '@/hooks/useMarketPartners';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { PartnerCard } from '@/components/gift-store/PartnerCard';
import { Skeleton } from '@/components/ui/skeleton';
import { trackGiftSuggestion } from '@/components/gift-store/giftStoreAnalytics';

// Map relationship type → preferred category slugs (in priority order)
const RELATIONSHIP_CATEGORY_MAP: Record<string, string[]> = {
  parent: ['meaningful-keepsakes', 'whole-self-health', 'express-tangibly'],
  grandparent: ['meaningful-keepsakes', 'express-tangibly', 'celebrate-appreciate'],
  child: ['celebrate-appreciate', 'meaningful-keepsakes', 'express-tangibly'],
  sibling: ['shared-experiences', 'celebrate-appreciate', 'express-tangibly'],
  spouse: ['shared-experiences', 'meaningful-keepsakes', 'express-tangibly'],
  friend: ['celebrate-appreciate', 'shared-experiences', 'express-tangibly'],
  mentor: ['meaningful-keepsakes', 'express-tangibly', 'celebrate-appreciate'],
  colleague: ['celebrate-appreciate', 'express-tangibly', 'meaningful-keepsakes'],
  crush: ['express-tangibly', 'meaningful-keepsakes', 'shared-experiences'],
  apology: ['healing-clarity', 'whole-self-health', 'express-tangibly'],
  closure: ['healing-clarity', 'whole-self-health', 'meaningful-keepsakes'],
  other: ['express-tangibly', 'celebrate-appreciate', 'meaningful-keepsakes'],
};

export default function GiftSuggestions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch announcement to get relationship type
  const { data: announcement } = useQuery({
    queryKey: ['announcement-for-gifts', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await (supabase as any)
        .from('announcements')
        .select('id, relationship, recipient_name')
        .eq('id', id)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!id,
  });

  const { data: allPartners = [], isLoading } = useMarketPartners();

  // Select 3 curated partners based on relationship
  const suggestedPartners = useMemo(() => {
    if (!allPartners.length || !announcement) return [];
    const relationship = announcement.relationship as string;
    const categorySlugs = RELATIONSHIP_CATEGORY_MAP[relationship] ?? RELATIONSHIP_CATEGORY_MAP.other;

    // Gather partners from preferred categories in order
    const result: typeof allPartners = [];
    for (const slug of categorySlugs) {
      const matches = allPartners.filter((p) => p.category_slug === slug);
      for (const p of matches) {
        if (!result.find((r) => r.id === p.id)) result.push(p);
        if (result.length >= 3) break;
      }
      if (result.length >= 3) break;
    }

    // Fill remainder from any category
    if (result.length < 3) {
      for (const p of allPartners) {
        if (!result.find((r) => r.id === p.id)) result.push(p);
        if (result.length >= 3) break;
      }
    }

    return result.slice(0, 3);
  }, [allPartners, announcement]);

  // Track impression
  useEffect(() => {
    if (id) trackGiftSuggestion('gift_suggestion_shown', id);
  }, [id]);

  const handleSkip = () => {
    if (id) trackGiftSuggestion('gift_suggestion_skipped', id);
    navigate('/dashboard');
  };

  const handleViewPartner = (slug: string) => {
    if (id) trackGiftSuggestion('gift_suggestion_clicked', id, slug);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-b from-primary/5 via-background to-background pt-16 pb-10 px-4">
          <div className="container max-w-2xl text-center mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full mb-5">
                <Heart className="h-3.5 w-3.5 fill-current" />
                <span className="text-xs font-semibold tracking-wide uppercase">Your words landed</span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3 leading-tight">
                Want to make it unforgettable?
              </h1>

              <p className="text-base text-muted-foreground max-w-md mx-auto mb-2">
                Sometimes a small gift carries your message even further.
              </p>
              {announcement?.recipient_name && (
                <p className="text-sm text-primary font-medium">
                  <Sparkles className="inline h-3.5 w-3.5 mr-1" />
                  Curated for your message to {announcement.recipient_name}
                </p>
              )}
            </motion.div>
          </div>
        </section>

        {/* Partner Cards */}
        <section className="px-4 pb-10 container max-w-3xl mx-auto">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-52 rounded-2xl" />
              ))}
            </div>
          ) : suggestedPartners.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {suggestedPartners.map((partner, i) => (
                <div key={partner.id} onClick={() => handleViewPartner(partner.slug)}>
                  <PartnerCard partner={partner} index={i} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>Browse our full gift store for ideas.</p>
              <Link to="/gift-store">
                <Button variant="outline" className="mt-4">Explore Gift Store</Button>
              </Link>
            </div>
          )}
        </section>

        {/* Actions */}
        <section className="px-4 pb-16 container max-w-3xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/gift-store">
              <Button variant="outline" className="gap-2">
                <ArrowRight className="h-4 w-4" />
                Browse all gift ideas
              </Button>
            </Link>
            <button
              onClick={handleSkip}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
              Skip — go to dashboard
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
